import { brandCopy } from '@/brand/head.ts'
import { BRAND_PROFILES, type BrandProfile } from '@/brand/profiles.ts'
import { detectLocale } from '@/i18n/locale.ts'
import { type BrandId, DEFAULT_BRAND_ID, normaliseBrandId } from '@/lib/branding.ts'

/**
 * Which brand this deployment is, resolved once.
 *
 * The value is read from the DOM rather than from configuration, and that is deliberate:
 * `boot.js` has already resolved it — from `window.__RALPH_BRAND__.id`, which the container
 * entrypoint wrote from `RALPH_BRAND_ID` — and stamped it on the document element before the
 * first paint, because the palette hangs off that attribute. Reading it back here means one
 * resolution rather than two that can disagree, exactly as `useTheme` reads `data-theme`
 * instead of consulting `localStorage` a second time.
 *
 * Module scope rather than a hook or a context. The brand cannot change while the page is
 * open — nothing in the product switches it, and the only thing that could is a container
 * restart, which is a reload — so a provider would be ceremony around a constant, and every
 * consumer would have to handle a "not yet known" state that never occurs.
 */

const resolve = (): BrandId => {
  if (typeof document === 'undefined') return DEFAULT_BRAND_ID
  return normaliseBrandId(document.documentElement.dataset.brand) ?? DEFAULT_BRAND_ID
}

export const brandId: BrandId = resolve()

export const brand: BrandProfile = BRAND_PROFILES[brandId]

/**
 * Put the profile's identity on the document — as a CORRECTION, not as the source.
 *
 * This used to be where the tab title, the description and the favicon came from, on the
 * reasoning that a caption settling a frame late is invisible while a desk in the wrong
 * colour is not. That is true of a browser and false of everything else: a link preview is
 * fetched by a crawler that runs no JavaScript, so on a deployment branded only from here,
 * every pasted link unfurled as the reference product (ADR-0014). The served document is now
 * chosen per brand by nginx and arrives already correct.
 *
 * It is kept because one case remains where the bytes are not this customer's: a fallback
 * document. An unrecognised `RALPH_BRAND_ID`, a `vite preview`, a plain static host — in each
 * of those the page is served as built and this is what still puts the right name in the tab.
 * On a correctly served deployment every assignment below is a no-op.
 *
 * The favicon link is looked up rather than created when the document already carries one,
 * so a deployment ends with a single icon rather than two the browser picks between.
 *
 * The description is prose and therefore per-locale (PRD-27 LOC-050), so it is read from the
 * catalogue rather than from the profile. `detectLocale()` and not `useI18n()`, because this
 * runs once outside React and before the first render — it reads back the language `boot.js`
 * already stamped on the document, which is the same one the provider will resolve to.
 * `productName` is a name and has no locale (D-17).
 */
export const applyBrandToDocument = (profile: BrandProfile = brand): void => {
  document.title = profile.productName

  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (description) description.content = brandCopy(detectLocale(), profile.id).description

  // The `type` moves with the `href`. index.html declares the shipped SVG icon, and a
  // deployment whose mark is pixels rather than paths would otherwise hand the browser a PNG
  // labelled `image/svg+xml` — which Safari believes, and then draws nothing at all.
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (icon && icon.getAttribute('href') !== profile.favicon) {
    icon.href = profile.favicon
    icon.type = profile.favicon.endsWith('.svg') ? 'image/svg+xml' : 'image/png'
  }
}
